<template>
  <div class="scoreboard-view">
    <h1>CFB Scoreboard</h1>

    <div class="filters">
      <div class="date-filter">
        <label for="date-select">Select Date:</label>
        <input
          type="date"
          id="date-select"
          v-model="selectedDate"
          @change="filterGamesByDate"
        />
      </div>

      <div class="conference-filter">
        <label for="conference-select">Filter by Conference:</label>
        <select
          id="conference-select"
          v-model="selectedConference"
          @change="filterGamesByConference"
        >
          <option value="">All Conferences</option>
          <option v-for="conf in conferences" :key="conf" :value="conf">
            {{ conf }}
          </option>
        </select>
      </div>

      <div class="search-filter">
        <label for="search-input">Search Teams:</label>
        <input
          type="text"
          id="search-input"
          v-model="searchQuery"
          placeholder="Search team names..."
          class="search-input"
          @input="filterGamesBySearch"
        />
      </div>
    </div>

    <div v-if="filteredGames.length === 0" class="no-games">
      <p>No games found for the selected criteria.</p>
    </div>

    <div v-else class="games-container">
      <div
        v-for="game in filteredGames"
        :key="game.id"
        class="game-card"
        @click="goToGameDetail(game.id)"
      >
        <div class="game-header">
          <span class="game-date">{{ formatDate(game.date) }}</span>
          <span class="game-status" :class="getStatusClass(game.status)">{{
            game.status.description
          }}</span>
        </div>

        <div class="game-teams">
          <div
            class="team"
            :class="{ 'home-team': team.is_home }"
            v-for="team in game.competitors"
            :key="team.id"
          >
            <div class="team-logo">
              <img :src="team.logo" :alt="team.display_name" />
            </div>
            <div class="team-info">
              <div class="team-name">{{ team.short_display_name }}</div>
              <div class="team-record">{{ team.records[0].summary }}</div>
              <div class="team-score" v-if="game.status.state !== 'pre'">
                {{ team.score || 0 }}
              </div>
            </div>
          </div>
        </div>

        <div class="game-footer">
          <span class="game-location">{{ game.competitors[0].location }}</span>
          <span class="game-weather" v-if="game.wthr">
            {{ game.wthr.temperature }}°F
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cookieUtils } from "../utils/cookieUtils";
import { timezoneUtils } from "../utils/timezoneUtils";

export default {
  name: "ScoreboardView",
  data() {
    return {
      games: [],
      filteredGames: [],
      selectedDate: this.getTodayDate(),
      selectedConference: "",
      searchQuery: "",
      conferences: [
        "ACC",
        "SEC",
        "Big Ten",
        "Big 12",
        "American",
        "CUSA",
        "Mountain West",
        "MAC",
        "Sun Belt",
      ],
    };
  },
  computed: {
    userTimezone() {
      return cookieUtils.getUserSelectedTimezone() || timezoneUtils.getUserTimezone();
    },
    use12HourFormat() {
      return cookieUtils.getUse12HourFormat();
    },
  },
  created() {
    this.loadScoreboardData();
  },
  methods: {
    async loadScoreboardData() {
      try {
        // Use the new game data loader
        const { loadLocalGameData } = await import("../utils/localDataLoader");
        this.games = await loadLocalGameData();
        this.filterGamesByDate();
      } catch (error) {
        console.error("Error loading scoreboard data:", error);
        this.games = this.getFallbackData();
        this.filterGamesByDate();
      }
    },
    getFallbackData() {
      // Return sample data for development/fallback
      return [
        {
          id: "401760370",
          date: "2025-09-14T03:59Z",
          name: "Portland State Vikings at Hawai'i Rainbow Warriors",
          short_name: "PRST @ HAW",
          competitors: [
            {
              id: "62",
              abbrev: "HAW",
              displayName: "Hawai'i Rainbow Warriors",
              shortDisplayName: "Hawai'i",
              logo: "https://a.espncdn.com/i/teamlogos/ncaa/500/62.png",
              isHome: true,
              records: [{ summary: "2-1" }],
              score: "17",
            },
            {
              id: "2502",
              abbrev: "PRST",
              displayName: "Portland State Vikings",
              shortDisplayName: "Portland St",
              logo: "https://a.espncdn.com/i/teamlogos/ncaa/500/2502.png",
              isHome: false,
              records: [{ summary: "0-3" }],
              score: "3",
            },
          ],
          status: {
            id: "2",
            description: "In Progress",
            state: "in",
            completed: false,
          },
          cnfrnce: "",
        },
      ];
    },
    filterGamesByDate() {
      // Start with all games
      if (!this.selectedDate) {
        this.filteredGames = [...this.games];
      } else {
        const selected = new Date(this.selectedDate);
        this.filteredGames = this.games.filter((game) => {
          const gameDate = new Date(game.date);
          return gameDate.toDateString() === selected.toDateString();
        });
      }

      // Apply other filters
      this.applyAllFilters();
    },
    filterGamesByConference() {
      this.applyAllFilters();
    },
    filterGamesBySearch() {
      this.applyAllFilters();
    },
    applyAllFilters() {
      // Start with all games or date-filtered games
      let result = [...this.games];

      // Apply date filter
      if (this.selectedDate) {
        const selected = new Date(this.selectedDate);
        result = result.filter((game) => {
          const gameDate = new Date(game.date);
          return gameDate.toDateString() === selected.toDateString();
        });
      }

      // Apply conference filter
      if (this.selectedConference) {
        result = result.filter((game) => {
          return game.cnfrnce === this.selectedConference;
        });
      }

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        result = result.filter((game) => {
          return game.competitors.some(
            (team) =>
              team.display_name.toLowerCase().includes(query) ||
              team.short_display_name.toLowerCase().includes(query) ||
              team.abbrev.toLowerCase().includes(query)
          );
        });
      }

      this.filteredGames = result;
    },
    formatDate(dateString) {
      return timezoneUtils.formatDate(dateString, this.userTimezone);
    },
    getStatusClass(status) {
      return {
        "status-pre": status.state === "pre",
        "status-in": status.state === "in",
        "status-post": status.state === "post",
      };
    },
    getTodayDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },
    goToGameDetail(gameId) {
      this.$router.push(`/game/${gameId}`);
    },
  },
};
</script>

<style scoped>
.scoreboard-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  text-align: center;
  margin-bottom: 30px;
  color: #c9d1d9;
}

.filters {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
}

.date-filter,
.conference-filter,
.search-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

select,
input[type="date"],
input[type="text"] {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #30363d;
  background-color: #0d1117;
  color: #e0e0e0;
}

.no-games {
  text-align: center;
  padding: 40px;
  background-color: #161b22;
  border-radius: 8px;
  border: 1px solid #30363d;
  color: #8b949e;
}

.games-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.game-card {
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 15px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #30363d;
}

.game-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.game-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #30363d;
}

.game-date {
  font-weight: 500;
  color: #8b949e;
}

.game-status {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-pre {
  background-color: #1e3a5f;
  color: #58a6ff;
}

.status-in {
  background-color: #4a3806;
  color: #ffd43b;
}

.status-post {
  background-color: #1e4620;
  color: #56d364;
}

.game-teams {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.team {
  display: flex;
  align-items: center;
  gap: 15px;
}

.team-logo {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-logo img {
  max-width: 100%;
  max-height: 100%;
}

.team-info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.team-name {
  font-weight: 600;
  color: #e0e0e0;
}

.team-record {
  font-size: 0.85rem;
  color: #8b949e;
}

.team-score {
  font-size: 1.5rem;
  font-weight: 700;
  color: #e0e0e0;
}

.home-team {
  background-color: #21262d;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #30363d;
}

.game-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid #30363d;
  font-size: 0.85rem;
  color: #8b949e;
}

@media (max-width: 768px) {
  .filters {
    flex-direction: column;
  }

  .games-container {
    grid-template-columns: 1fr;
  }
}
</style>
